export const createDateParser = (
  locale: string,
  createDate: (
    values?: {
      year?: number;
      month?: number;
      day?: number;
      hours?: number;
      minutes?: number;
      seconds?: number;
      milliseconds?: number;
    }
  ) => Date,
  parseDate: (dt: string) => Date,
  formatDate: (dt: Date, intlOptions?: any) => string
) : DateParser => {
  const getInputFormatFromLocale = (): string => {
    // ensure we use gregorian calender always
    const hasCalendar = locale.indexOf('-gregory') > -1;
    const calendarLocale = hasCalendar ? locale : `${locale}-u-ca-gregory`;
    const d = new Intl.DateTimeFormat(calendarLocale, { timeZone: 'UTC' });
    // create a new date with fixed day, month and year
    const dt = createDate({ year: 2001, month: 4, day: 22 });
    // let the gregorian Intl format format our date
    const placeholder = d.format(dt); // e.g "2/3/2001"
    // replace day, month and year with y/m/d placeholders
    return placeholder
      .replace(/2001/, 'YYYY')
      .replace(/0?5/, 'MM')
      .replace(/2?2/, 'DD');
  };

  const getInputFormatDescriptor: any = () => {
    try {
      //goal: split "yyyy-MM-dd" into {year:0, month:1, day:2}
      const format = getInputFormatFromLocale()
        .split(/\W/)
        .filter(x => !!x);
      const index = { y: 0, m: 0, d: 0 };

      for (let i = 0; i < format.length; i++) {
        //eslint:disable-line
        if (/[MM]/.test(format[i])) {
          index.m = i;
        } else if (/[DD]/.test(format[i])) {
          index.d = i;
        } else if (/[YYYY]/.test(format[i])) {
          index.y = i;
        }
      }
      return index;
    } catch (e) {
      return [];
    }
  };

  // eslint:disable-next-line:max-func-body-length
  const parseInputIntoYMDArray = (
    input: string,
    yearIndex: number,
    monthIndex: number
  ): string[] => {
      // TODO: return date
      //TODO: split into parsers-per-date-format, function is too big    
    let inputF = input.replace(/[\s]/g, '');
    switch (yearIndex) {
      case 0:
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        monthIndex === 1
          ? (inputF =
              inputF.length === 7
                ? Number(inputF.substr(4, 2)) > 12
                  ? inputF.replace(/^(\d\d\d\d)(\d?\d)(\d\d)$/, '$1 $2 $3')                     
                  : inputF.replace(/^(\d\d\d\d)(\d\d)(\d?\d)$/, '$1 $2 $3')                      
                : inputF.replace(/^(\d\d\d\d)(\d?\d)(\d?\d)$/, '$1 $2 $3'))
          : (inputF =
              inputF.length === 7 && Number(inputF.substr(5, 2)) <= 12
                ? inputF.replace(/^(\d\d\d\d)(\d?\d)(\d\d)$/, '$1 $2 $3')                    
                : inputF.length === 6
                  ? inputF.replace(/^(\d\d\d\d)(\d)(\d)$/, '$1 $2 $3')                      
                  : inputF.replace(/^(\d\d\d\d)(\d?\d)(\d?\d)$/, '$1 $2 $3'));
        break;
      case 1:
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        monthIndex === 0
          ? (inputF =
              inputF.length === 7
                ? Number(inputF.substr(0, 2)) > 12
                  ? inputF.replace(/^(\d?\d)(\d\d\d\d)(\d\d)$/, '$1 $2 $3')                      
                  : inputF.replace(/^(\d\d)(\d\d\d\d)(\d?\d)$/, '$1 $2 $3')                      
                : inputF.replace(/^(\d?\d)(\d\d\d\d)(\d?\d)$/, '$1 $2 $3'))
          : (inputF =
              inputF.length === 7 && Number(inputF.substr(5, 2)) <= 12
                ? inputF.replace(/^(\d?\d)(\d\d\d\d)(\d\d)$/, '$1 $2 $3')                    
                : inputF.length === 6
                  ? inputF.replace(/^(\d)(\d\d\d\d)(\d)$/, '$1 $2 $3')                      
                  : inputF.replace(/^(\d?\d)(\d\d\d\d)(\d?\d)$/, '$1 $2 $3'));
        break;
      case 2:
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        monthIndex === 0
          ? (inputF =
              inputF.length === 7
                ? Number(inputF.substr(0, 2)) > 12
                  ? inputF.replace(/^(\d?\d)(\d\d)(\d\d\d\d)$/, '$1 $2 $3')                      
                  : inputF.replace(/^(\d\d)(\d?\d)(\d\d\d\d)$/, '$1 $2 $3')                      
                : inputF.length === 6
                  ? inputF.replace(/^(\d\d)(\d\d)(\d\d)$/, '$1 $2 $3')                      
                  : Number(inputF.substr(0, 2)) > 12
                    ? inputF.replace(/^(\d)(\d?\d)(\d*)$/, '$1 $2 $3')                        
                    : inputF.replace(/^(\d\d)(\d?\d)(\d\d\d\d)$/, '$1 $2 $3'))
          : (inputF =
              inputF.length === 7 && Number(inputF.substr(1, 2)) <= 12
                ? inputF.replace(/^(\d?\d)(\d\d)(\d\d\d\d)$/, '$1 $2 $3')                    
                : inputF.length === 6
                  ? inputF.replace(/^(\d\d)(\d\d)(\d\d)$/, '$1 $2 $3')                      
                  : inputF.replace(/^(\d?\d)(\d?\d)(\d\d\d\d)$/, '$1 $2 $3'));
        break;
      default:
        break;
    }
    return inputF.split(/\W/).filter(s => !!s).map(w=>w.length > 4 ? w.substr(0,4) : w);
  };

  const isYMDValidDateArgs = (year: number, month: number, day: number): boolean => {
    if (!year || month < 0 || !day) {
      return false;
    }

    if (
      isNaN(year) ||
      isNaN(month) ||
      isNaN(day)
    ) {
      return false;
    }

    if (
      year < 1900 || year > 9999 || 
       month < 0 || month > 11 //JavaScript months are 0-11
      || day < 1 || day > 31
      ) {
        return false;
      }

    return true;
  };

  const parseToNewDate = (dateInput: string): Date => {
    try {
      const descriptor = getInputFormatDescriptor();
      const dateParts = parseInputIntoYMDArray(
        dateInput,
        descriptor.y,
        descriptor.m
      );
      
      let year = Number(dateParts[descriptor.y]);
      const month = Number(dateParts[descriptor.m]) - 1; // Calendar month vs JavaScript month
      const day = Number(dateParts[descriptor.d]);
     
      // 2-digit years to 4-digit, handle as 2000+
      if (year < 100 ) {
        const yearUTC = new Date().getUTCFullYear();
        const centuryFromYear = Math.floor(yearUTC / 100) + ((yearUTC % 100) ? 1 : 0);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        year < 10 ? year = Number(( centuryFromYear - 1 ).toString() + '0' + year ) :
          year = Number(( centuryFromYear - 1 ).toString() + year );
      }

      const isValidInput = isYMDValidDateArgs(year, month, day);
      return isValidInput ? createDate({year, month, day, hours: 0, minutes: 0, seconds: 0, milliseconds: 0}) : undefined;

    } catch (e) {
      return undefined;
    }
  };

  /**
   * 
   * @param shortcut example: ['d', '+', '1'] => add one day
   */
  const addDaysFromUserShortcut: any = ([shortcut, operator, days]: RegExpMatchArray): Date => {
    if (!shortcut || !shortcut.length) {      
      return undefined;
    }

    const result = createDate({
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    });
    
    let daysToAdd: number = Number(days) || 0;
        
    if (`${shortcut}`.match(/tomorrow/i)) {
      daysToAdd = 1;
      operator = '+';
    }

    if (`${shortcut}`.match(/yesterday/i)) {
      daysToAdd = 1;
      operator = '-';
    }
    
    if (operator && operator === '+'){
      result.setUTCDate(result.getUTCDate() + daysToAdd);
    }

    if (operator === '-'){
      result.setUTCDate(result.getUTCDate() - daysToAdd);
    } 
    
    return isYMDValidDateArgs(result.getUTCFullYear(), result.getUTCMonth(), result.getUTCDate()) ? result : undefined;
  };

  const addMonthsFromUserShortcut: any = ([shortcut, operator, months]: RegExpMatchArray): Date => {
    if (!shortcut || !shortcut.length) {
      return undefined;
    }
    const result = createDate({
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    });
    
    const monthsToAdd = Number(months) || 0;    
   
    if ( operator === '+'){
      result.setUTCMonth(result.getUTCMonth() + monthsToAdd);
    }

    if ( operator === '-'){
      result.setUTCMonth(result.getUTCMonth() - monthsToAdd);
    }    
  
  return isYMDValidDateArgs(result.getUTCFullYear(), result.getUTCMonth(), result.getUTCDate()) ? result : undefined;    
  };

  const addYearsFromUserShortcut: any = ([shortcut, operator, years]: RegExpMatchArray) : Date => {
    if (!shortcut || !shortcut.length) {
      return undefined;
    }
    const result = createDate({
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    });
    
    const yearsToAdd: number = Number(years) || 0;   
    
    if (operator === '+'){
      result.setUTCFullYear(result.getUTCFullYear() + yearsToAdd);
    }
    
    if (operator === '-'){
      result.setUTCFullYear(result.getUTCFullYear() - yearsToAdd);
    }

    return isYMDValidDateArgs(result.getUTCFullYear(), result.getUTCMonth(), result.getUTCDate()) ? result : undefined;    
  };

  const parseDateFromUserShortcut = (input: string): Date => {
    
    const hasSpecialCharacters = input.match(/[!@#$%^&*(),.?":{}|<>]/g);
    if(hasSpecialCharacters){
      return undefined;
    }
    const matchedDayShortcut = input.match(
      /^\s*?(now|today|tomorrow|yesterday|d|n|t)?(?![\w\d])(\+|\-)?(\d*)?\s?/i
    );
    const matchedDayShortcutDayPart = matchedDayShortcut && matchedDayShortcut.slice(1)[0] || 'd';  //default to 'd'
    const matchedDayShortcutOperatorPart = matchedDayShortcut && matchedDayShortcut.slice(1)[1];
    const matchedDayShortcutDaysPart = matchedDayShortcut && matchedDayShortcut.slice(1)[2];    
    const calculatedDateFromDayShortcut: Date = matchedDayShortcut && 
      addDaysFromUserShortcut([matchedDayShortcutDayPart, matchedDayShortcutOperatorPart, matchedDayShortcutDaysPart]);
    
    const matchedMonthShortcut = input.match(/^\s*?([m])(\+|\-)?(\d*)?\s?/i);
    const calculatedDateFromMonthShortcut = matchedMonthShortcut && addMonthsFromUserShortcut(matchedMonthShortcut.slice(1));
    
    const matchedYearShortcut = input.match(/^\s*?([y])(\+|\-)?(\d*)?\s?/i);
    const calculatedDateFromYearShortcut = matchedYearShortcut && addYearsFromUserShortcut(matchedYearShortcut.slice(1));

    const [calculatedDateFromUserShortcut] = [
      calculatedDateFromDayShortcut, calculatedDateFromMonthShortcut, calculatedDateFromYearShortcut
    ].filter( (x: Date) => (x instanceof Date));    
    
    return calculatedDateFromUserShortcut;
  };

  const parseDateInputOrUserShortcut = (input: string) : Date => {
    if (!input) {
      return undefined;
    }
    const parsedDate: any = parseToNewDate(input) || parseDateFromUserShortcut(input);
    
    return parsedDate || undefined;
  };

  const isValidDate = (input: Date): boolean => !!input && !isNaN(input.getTime());
  
  //TODO: if a function must parse string into string, then it's a mix of parser and formatter, or a transformer
  // all parsers should create a Date from a string. This should be cleaned up to keep code maintainable.
  return {
    parseDateInputOrUserShortcut,
    parseInputIntoYMDArray,
    addYearsFromUserShortcut,
    addMonthsFromUserShortcut,
    addDaysFromUserShortcut,
    getInputFormatFromLocale,
    isValidDate
  };
};

export interface DateParser{
  parseDateInputOrUserShortcut: (input: string) => Date;
  
  parseInputIntoYMDArray: ( //Note: only exposed due to existing unit tests. Should be fixed
    input: string,
    yearIndex: number,
    monthIndex: number) => string[];

  addYearsFromUserShortcut: (match: RegExpMatchArray) => Date;
  addMonthsFromUserShortcut: (match: RegExpMatchArray) => Date;
  addDaysFromUserShortcut: (match: RegExpMatchArray) => Date;
  getInputFormatFromLocale: () => string;
  isValidDate: (dt: Date) => boolean;
}
